import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { DialogVariant } from './dialog-types';

/**
 * The backdrop scrim colors the CDK overlay backdrop, which lives outside this component's
 * view encapsulation — so it can't be a scoped stylesheet. The dialog owns it itself (no
 * separate export or consumer import): the first surface to render injects one `<style>`
 * into `<head>`. Idempotent, browser-only. Figma "Scrim/Overlay" (61041:7560): on-surface 10%.
 * Openers set the backdrop's class to `cmp-dialog-scrim`.
 */
let scrimInjected = false;
function injectScrimStyles(): void {
  if (scrimInjected || typeof document === 'undefined') {
    return;
  }
  scrimInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-cmp-dialog-scrim', '');
  style.textContent =
    '.cmp-dialog-scrim{background-color:' +
    'color-mix(in srgb, var(--surface-surface-sys-on-surface) 10%, transparent);}';
  document.head.appendChild(style);
}

/**
 * Internal presentational chrome for the dialog: the surface box, header (leading icon +
 * title + close, or a floating close), centered icon badge + title/description, the body
 * slot, and the actions footer. Rendered by BOTH the declarative `compsych-dialog` and the
 * imperative `DialogService` container, so the chrome has a single source of truth.
 *
 * Not part of the public API. Overlay/a11y state (role, aria-modal, focus, open) is owned
 * by the host (the CDK dialog container); `titleId`/`descId` are supplied by the host so its
 * `aria-labelledby`/`aria-describedby` can reference the rendered title/description elements.
 * The host renders `display: contents`, so `.cmp-dialog` is the layout box.
 */
@Component({
  selector: 'cmp-dialog-surface',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dialog-surface.html',
  styleUrl: './dialog-surface.css',
})
export class DialogSurfaceComponent {
  /** Layout type. */
  readonly variant = input<DialogVariant>('modal-leading-icon');
  /** Optional CSS-length width override (falls back to the variant width). */
  readonly width = input<string>('');
  /** Heading text. */
  readonly title = input<string>('');
  /** Supporting text (rendered for `centered` and `alert` variants). */
  readonly description = input<string>('');
  /** Lucide icon name (leading header icon / centered badge glyph). */
  readonly icon = input<string>('');
  /** Whether the close button is shown. */
  readonly dismissible = input<boolean>(true);
  /** Accessible label for the close button. */
  readonly closeLabel = input<string>('Close dialog');
  /** Whether the projected actions footer is rendered. */
  readonly showActions = input<boolean>(true);
  /** Id for the title element (host supplies it for `aria-labelledby`). */
  readonly titleId = input<string>('');
  /** Id for the description element (host supplies it for `aria-describedby`). */
  readonly descId = input<string>('');

  /** Emitted when the close button is activated. */
  readonly closeClick = output<void>();

  private readonly sanitizer = inject(DomSanitizer);

  constructor() {
    // Self-contained scrim: inject the backdrop style on first render (no separate export/import).
    injectScrimStyles();
  }

  /** Header (leading) icon markup at 24px. */
  protected readonly iconMarkup = computed<SafeHtml>(() => this.buildIcon(this.icon(), 24));
  /** Centered badge icon markup at 32px. */
  protected readonly centeredIconMarkup = computed<SafeHtml>(() => this.buildIcon(this.icon(), 32));
  /** Close (`x`) icon markup at 20px. */
  protected readonly closeMarkup = computed<SafeHtml>(() => this.buildIcon('x', 20));

  /**
   * Looks up a Lucide icon by name and injects an explicit width/height so it renders at the
   * right size when bound via `[innerHTML]` (Angular's CSS encapsulation does not reach
   * dynamically injected content). Matches the Card/Button icon helper.
   */
  private buildIcon(name: string, size: number): SafeHtml {
    const svg = (LUCIDE_ICONS[name] ?? '').replace('<svg ', `<svg width="${size}" height="${size}" `);
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}
